import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import './SelfAssessment.css';

const CheckIn06 = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        // Section 1: Reflection - The past month has been
        reflectionDate: '',
        pastMonthReflection: '',
        mostImpactfulHabit: '',
        easiestHabit: '',
        hardestHabit: '',

        // Section 2: Congratulations Letter
        congratsDate: '',
        congratsLetter: '',
        congratsSignature: '',

        // Section 3: Current Health Status - Metrics
        age: '',
        bloodPressure: '',
        weight: '',
        weightUnit: 'kg',
        height: '',
        heightUnit: 'cm',
        fastingBloodGlucose: '',
        fastingGlucoseUnit: 'mmol/L',
        waistCircumference: '',
        waistCircumferenceUnit: 'cm',
        appreciateAboutBody: '',

        // Section 4: Current Health Status - Improvements
        furtherImprovements: '',
        highlightedSymptoms: '',

        // Section 5: Symptom Table - 10 symptoms
        symptom1: '',
        symptom1Rating: '',
        symptom2: '',
        symptom2Rating: '',
        symptom3: '',
        symptom3Rating: '',
        symptom4: '',
        symptom4Rating: '',
        symptom5: '',
        symptom5Rating: '',
        symptom6: '',
        symptom6Rating: '',
        symptom7: '',
        symptom7Rating: '',
        symptom8: '',
        symptom8Rating: '',
        symptom9: '',
        symptom9Rating: '',
        symptom10: '',
        symptom10Rating: '',

        // Additional symptoms table (16 symptoms)
        insomnia: '',
        jointPain: '',
        sinusPressure: '',
        swelling: '',
        palpitations: '',
        abdominalPain: '',
        abdominalBloating: '',
        abdominalGas: '',
        heartburn: '',
        constipation: '',
        diarrhoea: '',
        excessiveThirst: '',
        excessiveHunger: '',
        anxiety: '',
        depression: '',
        periodPainED: '',

        // Section 6: Reflect - Four Pillars
        nutritionBreakfast: '',
        nutritionLunch: '',
        nutritionDinner: '',
        nutritionSnacks: '',
        nutritionWater: '',
        waterUnit: 'ml',
        sleepBedtime: '',
        sleepWaketime: '',
        sleepQuality: '',
        exerciseTypes: '',
        exerciseWorkoutDays: '',
        exerciseMinutes: '',
        stressStressors: '',
        stressManagement: ''
    });

    const [entryId, setEntryId] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });

    useEffect(() => {
        loadEntry();
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
    }, []);

    // Autosave
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (Object.values(formData).some(val => val)) {
                saveEntry();
            }
        }, 2000);

        return () => clearTimeout(timeout);
    }, [formData]);

    const loadEntry = async () => {
        try {
            // Load CheckIn06 data
            const response = await api.get('/journal', { params: { section: 'checkin-6' } });

            // Load CheckIn02 data to get symptoms
            const checkin02Response = await api.get('/journal', { params: { section: 'checkin-2' } });

            let loadedData = { ...formData };

            // If CheckIn02 exists, pre-populate symptoms from month1Symptom fields
            if (checkin02Response.data.entries && checkin02Response.data.entries.length > 0) {
                const checkin02Data = checkin02Response.data.entries[0].data;
                // Map month1Symptom1-10 to symptom1-10
                for (let i = 1; i <= 10; i++) {
                    const symptomKey = `symptom${i}`;
                    const month1SymptomKey = `month1Symptom${i}`;
                    if (checkin02Data[month1SymptomKey]) {
                        loadedData[symptomKey] = checkin02Data[month1SymptomKey];
                    }
                }
            }

            // Override with CheckIn06 data if it exists
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];

                // Merge entry data but preserve pre-populated symptoms if CheckIn06 symptoms are empty
                const mergedData = { ...loadedData };

                // Copy all fields from entry.data
                Object.keys(entry.data).forEach(key => {
                    // For symptom1-10, only override if the value is not empty
                    if (key.match(/^symptom[1-9]$|^symptom10$/)) {
                        if (entry.data[key] && entry.data[key].trim() !== '') {
                            mergedData[key] = entry.data[key];
                        }
                        // If empty, keep the pre-populated value from CheckIn02
                    } else {
                        mergedData[key] = entry.data[key];
                    }
                });

                loadedData = mergedData;
                setEntryId(entry._id);
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }

            setFormData(loadedData);
        } catch (error) {
            console.error('Error loading entry:', error);
        }
    };

    const saveEntry = async (includeStartTime = false) => {
        try {
            const payload = {
                section: 'checkin-6',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (entryId) {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            } else {
                const response = await api.post('/journal', payload);
                setEntryId(response.data.entry._id);
                setEntryMetadata({
                    lockedAt: response.data.entry.lockedAt,
                    startedAt: response.data.entry.startedAt,
                    createdAt: response.data.entry.createdAt
                });
            }
        } catch (error) {
            console.error('Error saving entry:', error);
        }
    };

    const handleChange = (field, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    // Function to handle text selection in the improvements textarea
    const handleTextSelection = () => {
        const selectedText = window.getSelection().toString().trim();

        if (selectedText && selectedText.length > 1) {
            // Find the next available symptom slot (1-10)
            for (let i = 1; i <= 10; i++) {
                if (!formData[`symptom${i}`]) {
                    handleChange(`symptom${i}`, selectedText);

                    // Update the textarea content with bold formatting markers
                    const currentText = formData.furtherImprovements;

                    // Check if word is already marked
                    if (!currentText.includes(`**${selectedText}**`)) {
                        // Word not marked, mark it
                        const regex = new RegExp(`\\b${selectedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
                        const updatedText = currentText.replace(regex, `**${selectedText}**`);
                        handleChange('furtherImprovements', updatedText);
                    }

                    // Show user feedback
                    const notification = document.createElement('div');
                    notification.innerHTML = `✓ "${selectedText}" highlighted and added to symptom ${i}`;
                    notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: #4CAF50;
                        color: white;
                        padding: 12px 20px;
                        border-radius: 6px;
                        z-index: 1000;
                        font-size: 14px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        font-weight: 500;
                    `;
                    document.body.appendChild(notification);

                    setTimeout(() => {
                        if (document.body.contains(notification)) {
                            document.body.removeChild(notification);
                        }
                    }, 3000);

                    break;
                }
            }

            // Clear selection
            window.getSelection().removeAllRanges();
        }
    };

    // Function to render text with bold formatting for display
    const renderFormattedText = (text) => {
        if (!text) return '';
        // Replace **word** with bold HTML for display using CSS class
        return text.replace(/\*\*(.*?)\*\*/g, '<span class="highlighted-text">$1</span>');
    };

    // const isLocked = entryMetadata.lockedAt !== null;
    const isLocked = false;
    const modalConfig = getJournalStartConfig('checkin-06');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="self-assessment-container">
                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />

                <div className="journal-page-wrapper">
                    {/* Section 1: Reflection - The past month has been */}
                    <div>
                        <div className="journal-line" style={{ marginBottom: '40px', maxWidth: '200px' }}>
                            <label style={{ fontSize: '16px', display: 'block', marginBottom: '8px', color: '#2c2416' }}>Date</label>
                            <input
                                type="date"
                                value={formData.reflectionDate}
                                onChange={(e) => handleChange('reflectionDate', e.target.value)}
                                disabled={isLocked}
                            />
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px',
                                marginBottom: '20px'
                            }}>The past month has been</p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.pastMonthReflection}
                                    onChange={(e) => handleChange('pastMonthReflection', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px',
                                marginBottom: '20px'
                            }}>I've noticed that the most impactful habit was</p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.mostImpactfulHabit}
                                    onChange={(e) => handleChange('mostImpactfulHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                // fontSize: '22px',
                                marginBottom: '20px'
                            }}>The easiest habit to keep was</p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.easiestHabit}
                                    onChange={(e) => handleChange('easiestHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '40px' }}>
                            <p className="handwritten-label" style={{
                                //  fontSize: '22px',
                                marginBottom: '20px'
                            }}>The hardest habit to keep was</p>
                            <div className="journal-line multi-line">
                                <textarea
                                    value={formData.hardestHabit}
                                    onChange={(e) => handleChange('hardestHabit', e.target.value)}
                                    placeholder=""
                                    rows={4}
                                    style={{ width: '100%', lineHeight: '32px' }}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Congratulations */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '30px', marginBottom: '40px' }}>Congratulations!</h2>

                        <p className="journal-prompt" style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '40px', textAlign: 'justify' }}>
                            Congratulations on staying the course and investing in your health! It may not have been easy all the
                            time, but you found a way to make it to Month 3. I hope that you've been able to experience
                            improvements in your mind and body. The journey does not end here, this was practice for the lifestyle
                            that you could lead. How does life look now? Write a letter to the version of you who began this journey
                            three months ago.
                        </p>

                        <p className="handwritten-label" style={{
                            // fontSize: '24px',
                            marginBottom: '30px'
                        }}>Dear me,</p>

                        <div className="journal-line multi-line" style={{ marginBottom: '60px' }}>
                            <textarea
                                value={formData.congratsLetter}
                                onChange={(e) => handleChange('congratsLetter', e.target.value)}
                                placeholder=""
                                rows={16}
                                style={{ width: '100%', lineHeight: '32px' }}
                                disabled={isLocked}
                            />
                        </div>

                        <div
                            className="date-signature"
                        >
                            <div className="signature-field">
                                <p className="handwritten-label" style={{ marginBottom: '10px' }}>
                                    With great love,
                                </p>

                                <input
                                    type="text"
                                    value={formData.congratsSignature}
                                    onChange={(e) => handleChange('congratsSignature', e.target.value)}
                                    placeholder="Me"
                                    disabled={isLocked}
                                />
                            </div>
                            <div className="date-field">
                                <div className="handwritten-label" style={{ marginBottom: '10px' }}>
                                    Date
                                </div>
                                <input
                                    type="date"
                                    value={formData.congratsDate}
                                    onChange={(e) => handleChange('congratsDate', e.target.value)}
                                    disabled={isLocked}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Current Health Status - Metrics */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Current health status</h2>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '40px' }}>
                            Reminder: you're right on time
                        </p>

                        <div className="month3-health-metrics-grid">
                            <div className="journal-line">
                                <label>Age</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="150"
                                    value={formData.age}
                                    onChange={(e) => handleChange('age', e.target.value)}
                                    placeholder="0"
                                    disabled={isLocked}
                                    onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                />
                            </div>
                            <div className="journal-line">
                                <label>Blood Pressure (mmHg)</label>
                                <input
                                    type="text"
                                    value={formData.bloodPressure}
                                    onChange={(e) => {
                                        // Only allow numbers, decimals, and forward slash
                                        const value = e.target.value.replace(/[^0-9./]/g, '');
                                        handleChange('bloodPressure', value);
                                    }}
                                    placeholder="120.5/80.0"
                                    pattern="^[0-9]+\.?[0-9]*\/[0-9]+\.?[0-9]*$"
                                    disabled={isLocked}
                                />
                            </div>
                            <div className="journal-line">
                                <label>Weight</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={formData.weight}
                                        onChange={(e) => handleChange('weight', e.target.value)}
                                        placeholder="0.0"
                                        style={{ flex: 1 }}
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                    <select
                                        value={formData.weightUnit}
                                        onChange={(e) => handleChange('weightUnit', e.target.value)}
                                        className='dropdown-style'
                                        // style={{
                                        //     padding: '8px',
                                        //     border: '1px solid #ccc',
                                        //     borderRadius: '4px',
                                        //     fontSize: '14px',
                                        //     minWidth: '80px'
                                        // }}
                                        disabled={isLocked}
                                    >
                                        <option value="kg">kg</option>
                                        <option value="lbs">lbs</option>
                                    </select>
                                </div>
                            </div>
                            <div className="journal-line">
                                <label>Height</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={formData.height}
                                        onChange={(e) => handleChange('height', e.target.value)}
                                        placeholder="170"
                                        style={{ flex: 1 }}
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                    <select
                                        value={formData.heightUnit}
                                        onChange={(e) => handleChange('heightUnit', e.target.value)}
                                        className='dropdown-style'
                                        // style={{
                                        //     padding: '8px',
                                        //     border: '1px solid #ccc',
                                        //     borderRadius: '4px',
                                        //     fontSize: '14px',
                                        //     minWidth: '80px'
                                        // }}
                                        disabled={isLocked}
                                    >
                                        <option value="cm">cm</option>
                                        <option value="ft">ft</option>
                                    </select>
                                </div>
                            </div>
                            <div className="journal-line">
                                <label>Fasting blood glucose</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.fastingBloodGlucose}
                                        onChange={(e) => handleChange('fastingBloodGlucose', e.target.value)}
                                        placeholder="0.00"
                                        style={{ flex: 1 }}
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                    <select
                                        value={formData.fastingGlucoseUnit}
                                        onChange={(e) => handleChange('fastingGlucoseUnit', e.target.value)}
                                        className='dropdown-style'
                                        // style={{
                                        //     padding: '8px',
                                        //     border: '1px solid #ccc',
                                        //     borderRadius: '4px',
                                        //     fontSize: '14px',
                                        //     minWidth: '80px'
                                        // }}
                                        disabled={isLocked}
                                    >
                                        <option value="mmol/L">mmol/L</option>
                                        <option value="mg/dL">mg/dL</option>
                                    </select>
                                </div>
                            </div>
                            <div className="journal-line">
                                <label>Waist circumference</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.waistCircumference}
                                        onChange={(e) => handleChange('waistCircumference', e.target.value)}
                                        placeholder="0.00"
                                        style={{ flex: 1 }}
                                        disabled={isLocked}
                                        onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                    />
                                    <select
                                        value={formData.waistCircumferenceUnit}
                                        onChange={(e) => handleChange('waistCircumferenceUnit', e.target.value)}
                                        className='dropdown-style'
                                        // style={{
                                        //     padding: '8px',
                                        //     border: '1px solid #ccc',
                                        //     borderRadius: '4px',
                                        //     fontSize: '14px',
                                        //     minWidth: '80px'
                                        // }}
                                        disabled={isLocked}
                                    >
                                        <option value="cm">cm</option>
                                        <option value="in">inches</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '20px' }}>
                            List everything that you appreciate about your mind and body:
                        </p>

                        <div className="journal-line multi-line">
                            <textarea
                                value={formData.appreciateAboutBody}
                                onChange={(e) => handleChange('appreciateAboutBody', e.target.value)}
                                placeholder=""
                                rows={12}
                                style={{ width: '100%', lineHeight: '32px' }}
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Section 4: Current Health Status - Improvements */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Current health status</h2>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '20px' }}>
                            What further improvements could you make to your overall health?
                        </p>

                        <div style={{
                            background: '#f0f8ff',
                            padding: '15px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #b0d4f1'
                        }}>
                            <p style={{
                                fontSize: '13px',
                                color: '#2c5aa0',
                                margin: '0',
                                lineHeight: '1.5'
                            }}>
                                💡 <strong>Tip:</strong> Select/highlight any words or phrases in the text area below.
                                They will automatically appear in the symptom rating fields for you to rate.
                            </p>
                        </div>

                        <div className="journal-line multi-line" style={{ marginBottom: '60px', position: 'relative' }}>
                            {/* Overlay div for displaying bold formatted text */}
                            <div
                                id="improvements-overlay-div"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    padding: '8px 0',
                                    border: 'none',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    lineHeight: '32px',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    overflow: 'auto',
                                    background: 'transparent',
                                    paddingTop: 0
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: renderFormattedText(formData.furtherImprovements)
                                }}
                            />

                            {/* Actual textarea with original styling */}
                            <textarea
                                id="improvements-textarea"
                                value={formData.furtherImprovements}
                                onChange={(e) => {
                                    handleChange('furtherImprovements', e.target.value);
                                    // Sync scroll position
                                    const textarea = e.target;
                                    const overlay = document.getElementById('improvements-overlay-div');
                                    if (overlay) {
                                        overlay.scrollTop = textarea.scrollTop;
                                    }
                                }}
                                onScroll={(e) => {
                                    // Sync scroll position
                                    const textarea = e.target;
                                    const overlay = document.getElementById('improvements-overlay-div');
                                    if (overlay) {
                                        overlay.scrollTop = textarea.scrollTop;
                                    }
                                }}
                                onMouseUp={handleTextSelection}
                                onKeyUp={handleTextSelection}
                                placeholder="Start typing health improvement goals and concerns here..."
                                rows={16}
                                style={{
                                    position: 'relative',
                                    zIndex: 2,
                                    backgroundColor: 'transparent',
                                    color: 'transparent',
                                    caretColor: '#2c2416', // Show cursor
                                    userSelect: 'text',
                                    cursor: 'text',
                                    width: '100%',
                                    lineHeight: '32px',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    resize: 'vertical',
                                    border: 'none',
                                    outline: 'none',
                                    paddingTop: 0
                                }}
                                disabled={isLocked}
                            />
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '20px' }}>
                            Highlight or circle any symptoms (or keywords) that you have written on this page.
                        </p>

                        <div className="journal-line multi-line">
                            <textarea
                                value={formData.highlightedSymptoms}
                                onChange={(e) => handleChange('highlightedSymptoms', e.target.value)}
                                placeholder=""
                                rows={2}
                                style={{ width: '100%', lineHeight: '32px' }}
                                disabled={isLocked}
                            />
                        </div>
                    </div>

                    {/* Section 5: Symptom Rating Table */}
                    <div style={{ marginTop: '80px' }}>
                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '30px', lineHeight: '1.7' }}>
                            List the keywords that you have highlighted and give them a rating out of 10.
                        </p>

                        <div style={{
                            background: '#f0f8ff',
                            padding: '15px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #b0d4f1'
                        }}>
                            <p style={{
                                fontSize: '13px',
                                color: '#2c5aa0',
                                margin: '0',
                                lineHeight: '1.5'
                            }}>
                                💡 <strong>Tip:</strong> Go back to the text area above and select/highlight any words or phrases.
                                They will automatically appear in the numbered fields below for rating.
                            </p>
                        </div>

                        <p style={{ fontSize: '13px', marginBottom: '10px', color: '#2c2416' }}>
                            1 = not severe, intense or frequent (noticeable but very manageable)
                        </p>
                        <p style={{ fontSize: '13px', marginBottom: '40px', color: '#2c2416' }}>
                            10 = very severe, intense or frequent (greatly affects daily life)
                        </p>

                        {/* Two Column Layout for 10 symptoms */}
                        <div className="symptom-rating-container" style={{ marginBottom: '60px' }}>
                            {[1, 2, 3, 4, 5].map(num => (
                                <div key={num} className="symptom-rating-row">
                                    {/* Left Column */}
                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num}.</strong>
                                        <div style={{ flex: 1, position: 'relative', marginBottom: 0 }}>
                                            <input
                                                type="text"
                                                value={formData[`symptom${num}`]}
                                                onChange={(e) => handleChange(`symptom${num}`, e.target.value)}
                                                placeholder=""
                                                style={{
                                                    width: '100%',
                                                    padding: '15px 10px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #d4c4a8',
                                                    background: formData[`symptom${num}`] ? 'transparent' : 'transparent',
                                                    paddingRight: formData[`symptom${num}`] ? '30px' : '10px',
                                                    fontSize: 'inherit',
                                                    fontFamily: 'inherit',
                                                    outline: 'none'
                                                }}
                                                disabled={isLocked}
                                            />
                                            {formData[`symptom${num}`] && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange(`symptom${num}`, '')}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '8px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#999',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        lineHeight: '1'
                                                    }}
                                                    title="Clear this symptom"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        <div className="rating-input-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '80px' }}>
                                            <select
                                                value={formData[`symptom${num}Rating`] || ''}
                                                onChange={(e) => handleChange(`symptom${num}Rating`, e.target.value)}
                                                className="rating-input"
                                                style={{
                                                    fontFamily: 'Vintage Moon',
                                                    fontSize: '24.5px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    borderBottom: '1px solid #d4c4a8',
                                                    outline: 'none',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    width: '50px'
                                                }}
                                                disabled={isLocked}
                                            >
                                                <option value="">-</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                                <option value="5">5</option>
                                                <option value="6">6</option>
                                                <option value="7">7</option>
                                                <option value="8">8</option>
                                                <option value="9">9</option>
                                                <option value="10">10</option>
                                            </select>
                                            <span className="rating-divider" style={{
                                                fontFamily: 'Vintage Moon',
                                                fontSize: '24.5px',
                                                color: '#2c2416'
                                            }}>/10</span>
                                        </div>
                                    </div>
                                    {/* Right Column */}
                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num + 5}.</strong>
                                        <div style={{ flex: 1, position: 'relative', marginBottom: 0 }}>
                                            <input
                                                type="text"
                                                value={formData[`symptom${num + 5}`]}
                                                onChange={(e) => handleChange(`symptom${num + 5}`, e.target.value)}
                                                placeholder=""
                                                style={{
                                                    width: '100%',
                                                    padding: '15px 10px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #d4c4a8',
                                                    background: formData[`symptom${num + 5}`] ? 'transparent' : 'transparent', //#e8f5e8
                                                    paddingRight: formData[`symptom${num + 5}`] ? '30px' : '10px',
                                                    fontSize: 'inherit',
                                                    fontFamily: 'inherit',
                                                    outline: 'none'
                                                }}
                                                disabled={isLocked}
                                            />
                                            {formData[`symptom${num + 5}`] && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange(`symptom${num + 5}`, '')}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '8px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#999',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        lineHeight: '1'
                                                    }}
                                                    title="Clear this symptom"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        <div className="rating-input-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '80px' }}>
                                            <select
                                                value={formData[`symptom${num + 5}Rating`] || ''}
                                                onChange={(e) => handleChange(`symptom${num + 5}Rating`, e.target.value)}
                                                className="rating-input"
                                                style={{
                                                    fontFamily: 'Vintage Moon',
                                                    fontSize: '24.5px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    borderBottom: '1px solid #d4c4a8',
                                                    outline: 'none',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    width: '50px'
                                                }}
                                                disabled={isLocked}
                                            >
                                                <option value="">-</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                                <option value="5">5</option>
                                                <option value="6">6</option>
                                                <option value="7">7</option>
                                                <option value="8">8</option>
                                                <option value="9">9</option>
                                                <option value="10">10</option>
                                            </select>
                                            <span className="rating-divider" style={{
                                                fontFamily: 'Vintage Moon',
                                                fontSize: '24.5px',
                                                color: '#2c2416'
                                            }}>/10</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '30px' }}>
                            Highlight any additional symptoms you have been experiencing and give it a rating out of ten.
                        </p>

                        {/* Additional Symptoms Grid Table */}
                        <div className="symptom-table-grid" style={{
                            border: '1px solid #8fa998',
                            gap: '0',
                            marginBottom: '40px'
                        }}>
                            {/* Header Row */}
                            <div className="symptom-table-header">Symptom</div>
                            <div className="symptom-table-header symptom-table-rating-header">Rating out of 10</div>
                            <div className="symptom-table-header symptom-table-header-right">Symptom</div>
                            <div className="symptom-table-header symptom-table-rating-header symptom-table-header-last">Rating out of 10</div>

                            {/* Row 1 */}
                            <div className="symptom-table-cell symptom-cell-dark">insomnia</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.insomnia}
                                    onChange={(e) => handleChange('insomnia', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">heartburn</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.heartburn}
                                    onChange={(e) => handleChange('heartburn', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 2 */}
                            <div className="symptom-table-cell symptom-cell-light">joint pain</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.jointPain}
                                    onChange={(e) => handleChange('jointPain', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">constipation</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.constipation}
                                    onChange={(e) => handleChange('constipation', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 3 */}
                            <div className="symptom-table-cell symptom-cell-dark">sinus pressure</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.sinusPressure}
                                    onChange={(e) => handleChange('sinusPressure', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">diarrhoea</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.diarrhoea}
                                    onChange={(e) => handleChange('diarrhoea', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 4 */}
                            <div className="symptom-table-cell symptom-cell-light">swelling</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.swelling}
                                    onChange={(e) => handleChange('swelling', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">excessive thirst</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.excessiveThirst}
                                    onChange={(e) => handleChange('excessiveThirst', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 5 */}
                            <div className="symptom-table-cell symptom-cell-dark">palpitations</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.palpitations}
                                    onChange={(e) => handleChange('palpitations', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">excessive hunger</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.excessiveHunger}
                                    onChange={(e) => handleChange('excessiveHunger', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 6 */}
                            <div className="symptom-table-cell symptom-cell-light">abdominal pain</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.abdominalPain}
                                    onChange={(e) => handleChange('abdominalPain', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">anxiety</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.anxiety}
                                    onChange={(e) => handleChange('anxiety', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 7 */}
                            <div className="symptom-table-cell symptom-cell-dark">abdominal bloating</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.abdominalBloating}
                                    onChange={(e) => handleChange('abdominalBloating', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-dark">depression</div>
                            <div className="symptom-table-cell rating-cell-light">
                                <select
                                    value={formData.depression}
                                    onChange={(e) => handleChange('depression', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>

                            {/* Row 8 */}
                            <div className="symptom-table-cell symptom-cell-light">abdominal gas</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.abdominalGas}
                                    onChange={(e) => handleChange('abdominalGas', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                            <div className="symptom-table-cell symptom-cell-light">period pain/erectile dysfunction</div>
                            <div className="symptom-table-cell rating-cell-lighter">
                                <select
                                    value={formData.periodPainED}
                                    onChange={(e) => handleChange('periodPainED', e.target.value)}
                                    className="symptom-rating-table-input"
                                    style={{
                                        fontFamily: 'Vintage Moon',
                                        fontSize: '20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                    disabled={isLocked}
                                >
                                    <option value="">-</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                    <option value="8">8</option>
                                    <option value="9">9</option>
                                    <option value="10">10</option>
                                </select>
                            </div>
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '13px', lineHeight: '1.7', marginBottom: '20px' }}>
                            Reminder: if you haven't received a medical explanation for your symptoms, especially if they are severe, it's highly
                            recommended that you speak to a qualified health professional.
                        </p>
                    </div>

                    {/* Section 6: Reflect - Four Pillars */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Reflect</h2>

                        <p className="journal-prompt" style={{ fontSize: '14px', marginBottom: '40px' }}>
                            Below are the four pillars of health. How does your lifestyle look right now?
                        </p>

                        {/* Four Quadrants Grid */}
                        <div className="reflect-grid">
                            {/* Top Left: Nutrition */}
                            <div className="reflect-section nutrition-section" style={{ borderRight: '1px solid #2c2416', borderBottom: '1px solid #2c2416' }}>
                                <h3>Nutrition</h3>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Breakfast</label>
                                    <div className="journal-line">
                                        <input
                                            type="text"
                                            value={formData.nutritionBreakfast}
                                            onChange={(e) => handleChange('nutritionBreakfast', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Lunch</label>
                                    <div className="journal-line">
                                        <input
                                            type="text"
                                            value={formData.nutritionLunch}
                                            onChange={(e) => handleChange('nutritionLunch', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Dinner</label>
                                    <div className="journal-line">
                                        <input
                                            type="text"
                                            value={formData.nutritionDinner}
                                            onChange={(e) => handleChange('nutritionDinner', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Snacks</label>
                                    <div className="journal-line">
                                        <input
                                            type="text"
                                            value={formData.nutritionSnacks}
                                            onChange={(e) => handleChange('nutritionSnacks', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Water</label>
                                    <div className="journal-line" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.nutritionWater}
                                            onChange={(e) => handleChange('nutritionWater', e.target.value)}
                                            placeholder="0000"
                                            style={{
                                                flex: 1,
                                                border: 'none',
                                                background: 'transparent',
                                                outline: 'none',
                                                fontSize: 'inherit',
                                                fontFamily: 'inherit',
                                                padding: '0'
                                            }}
                                            disabled={isLocked}
                                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                        />
                                        <select
                                            value={formData.waterUnit}
                                            onChange={(e) => handleChange('waterUnit', e.target.value)}
                                            className='dropdown-style'
                                            // style={{
                                            //     padding: '8px',
                                            //     border: '1px solid #ccc',
                                            //     borderRadius: '4px',
                                            //     fontSize: '14px',
                                            //     minWidth: '80px'
                                            // }}
                                            disabled={isLocked}
                                        >
                                            <option value="ml">ml</option>
                                            <option value="ounce">ounce</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Top Right: Sleep */}
                            <div className="reflect-section sleep-section" style={{ borderBottom: '1px solid #2c2416' }}>
                                <h3>Sleep</h3>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Bed-time</label>
                                    <div className="journal-line">
                                        <input
                                            type="time"
                                            value={formData.sleepBedtime}
                                            onChange={(e) => handleChange('sleepBedtime', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Wake-time</label>
                                    <div className="journal-line">
                                        <input
                                            type="time"
                                            value={formData.sleepWaketime}
                                            onChange={(e) => handleChange('sleepWaketime', e.target.value)}
                                            placeholder=""
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Sleep quality</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {['Great!', 'Good', 'Okay', 'Poor', 'Terrible!'].map(quality => (
                                            <label key={quality} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="sleepQuality"
                                                    value={quality}
                                                    checked={formData.sleepQuality === quality}
                                                    onChange={(e) => handleChange('sleepQuality', e.target.value)}
                                                    style={{ marginRight: '10px', cursor: 'pointer' }}
                                                    disabled={isLocked}
                                                />
                                                <span style={{ fontSize: '14px' }}>{quality}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Left: Exercise */}
                            <div className="reflect-section exercise-section" style={{ borderRight: '1px solid #2c2416' }}>
                                <h3>Exercise</h3>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Types of exercise</label>
                                    <div className="journal-line multi-line">
                                        <textarea
                                            value={formData.exerciseTypes}
                                            onChange={(e) => handleChange('exerciseTypes', e.target.value)}
                                            placeholder=""
                                            rows={3}
                                            style={{ lineHeight: '28px' }}
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>No. of work-out days per week</label>
                                    <div className="journal-line" style={{ maxWidth: '150px' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="7"
                                            value={formData.exerciseWorkoutDays}
                                            onChange={(e) => handleChange('exerciseWorkoutDays', e.target.value)}
                                            placeholder="0"
                                            style={{ width: '80px' }}
                                            disabled={isLocked}
                                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>Minutes of exercise per session</label>
                                    <div className="journal-line" style={{ maxWidth: '150px' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.exerciseMinutes}
                                            onChange={(e) => handleChange('exerciseMinutes', e.target.value)}
                                            placeholder="000"
                                            style={{ width: '120px' }}
                                            disabled={isLocked}
                                            onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Right: Stress Management */}
                            <div className="reflect-section stress-section">
                                <h3>Stress management</h3>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>What are your stressors?</label>
                                    <div className="journal-line multi-line">
                                        <textarea
                                            value={formData.stressStressors}
                                            onChange={(e) => handleChange('stressStressors', e.target.value)}
                                            placeholder=""
                                            rows={4}
                                            style={{ lineHeight: '28px' }}
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '14px', display: 'block', marginBottom: '10px' }}>How do you manage stress?</label>
                                    <div className="journal-line multi-line">
                                        <textarea
                                            value={formData.stressManagement}
                                            onChange={(e) => handleChange('stressManagement', e.target.value)}
                                            placeholder=""
                                            rows={4}
                                            style={{ lineHeight: '28px' }}
                                            disabled={isLocked}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 7: Stay in Touch - Contact */}
                    <div style={{ marginTop: '80px' }}>
                        <h2 className="journal-title" style={{ fontSize: '40px', marginBottom: '40px' }}>Contact</h2>

                        <p className="journal-prompt" style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '30px', textAlign: 'justify' }}>
                            I hope that this journal has helped you to navigate your health in a way that inspires you to be keep
                            progressing. Your wellbeing is your responsibility and it's also something that you can approach
                            creatively and uniquely - no two people are the same.
                        </p>

                        <p className="journal-prompt" style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '50px', textAlign: 'justify' }}>
                            If this book has helped you to have a better experience in your body and mind, please feel free to
                            contact me. I would love to hear all about it - no matter how big or small.
                        </p>

                        {/* Contact Info */}
                        <div style={{ marginBottom: '60px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                <span style={{ fontSize: '20px', marginRight: '15px' }}>🌐</span>
                                <a href="https://www.drwithin.shop" target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: '16px', color: '#2c2416', textDecoration: 'none' }}>
                                    www.drwithin.shop
                                </a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                <span style={{ fontSize: '20px', marginRight: '15px' }}>✉️</span>
                                <a href="mailto:info@drwithin.shop"
                                    style={{ fontSize: '16px', color: '#2c2416', textDecoration: 'none' }}>
                                    info@drwithin.shop
                                </a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                <span style={{ fontSize: '20px', marginRight: '15px' }}>📱</span>
                                <a href="https://www.instagram.com/dr.with.in" target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: '16px', color: '#2c2416', textDecoration: 'none' }}>
                                    @dr.with.in
                                </a>
                            </div>
                        </div>

                        {/* Personalized Guide Section */}
                        <div style={{ marginBottom: '50px' }}>
                            <h3 style={{ fontSize: '24px', marginBottom: '15px', fontWeight: '500' }}>
                                Would you like a personalized guide?
                            </h3>
                            <p style={{ fontSize: '20px', fontStyle: 'italic', marginBottom: '40px' }}>
                                Talk to a naturopath online in a wellness one-on-one
                            </p>

                            {/* QR Code Placeholder */}
                            <div style={{
                                width: '250px',
                                height: '250px',
                                margin: '0 auto 40px',
                                // border: '2px solid #2c2416',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff'
                            }}>
                                <img src="/QR.png" alt="QR Code" style={{ maxWidth: '90%', maxHeight: '90%' }} />
                            </div>
                        </div>

                        <p className="journal-prompt" style={{ fontSize: '13px', lineHeight: '1.7', textAlign: 'justify' }}>
                            Sometimes, we invest a lot of time and effort into our health and feel no differences. This is possible, especially
                            when the underlying causes have not been addressed. If you believe that you did not reach your expectations
                            despite thoroughly following a plan, and would like more personalized guidelines, you may also contact me through
                            any of the mediums above or book a wellness one-on-one.
                        </p>

                        <JournalNavigation
                            prevPage="/journal/checkin-05"
                            nextPage={null}
                        />
                    </div>
                </div>

            </div>        </>
    );
};

export default CheckIn06;
